import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import classnames from 'classnames';
import { StyledScaleContainer, StyledScaleBox, ROOT_PREFIX } from './scaleSelectionStyles';
import { onKeyPress } from '../utils/wcagUtils';
export function EmbersScaleSelection(_ref) {
  var id = _ref.id,
    value = _ref.value,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? '' : _ref$label,
    labelHidden = _ref.labelHidden,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'value' : _ref$valueKey,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'label' : _ref$labelKey,
    _ref$ariaKey = _ref.ariaKey,
    ariaKey = _ref$ariaKey === void 0 ? 'ariaLabel' : _ref$ariaKey,
    _ref$scales = _ref.scales,
    scales = _ref$scales === void 0 ? [] : _ref$scales,
    _ref$style = _ref.style,
    style = _ref$style === void 0 ? {} : _ref$style,
    disabled = _ref.disabled,
    onChange = _ref.onChange;
  var refTextSpanArray = useRef([]);
  var getRef = function getRef(dom) {
    refTextSpanArray.current.push(dom);
  };
  var _useState = useState(16),
    _useState2 = _slicedToArray(_useState, 2),
    labelHeight = _useState2[0],
    setLabelHeight = _useState2[1];
  // helps with accessibility and tab focus
  var _useState3 = useState(-1),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedIndex = _useState4[0],
    setSelectedIndex = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasFocus = _useState6[0],
    setHasFocus = _useState6[1];
  var hasFocusRef = React.useRef(false);
  var selectedIndexRef = React.useRef(selectedIndex);
  var updateIndexState = function updateIndexState(index) {
    setSelectedIndex(index);
    selectedIndexRef.current = index;
  };
  var scaleSize = scales.length - 1;
  var handleKeyDown = function handleKeyDown(e) {
    if (!hasFocusRef.current) return;
    var index = selectedIndexRef.current;
    e.key !== 'Tab' && e.preventDefault();
    switch (e.key) {
      case 'Home':
        onDataChange(0);
        break;
      case 'End':
        onDataChange(scaleSize);
        break;
      case 'Right': // IE/Edge specific value
      case 'ArrowRight':
      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        index !== scaleSize && onDataChange(index + 1);
        break;
      case 'Left': // IE/Edge specific value
      case 'ArrowLeft':
      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
        index !== 0 && onDataChange(index - 1);
        break;
      default:
        break;
    }
  };
  useEffect(function () {
    hasFocusRef.current = hasFocus;
  }, [hasFocus]);

  // To make sure the container can be flexible height when has multiple text line
  useEffect(function () {
    var spanArray = refTextSpanArray === null || refTextSpanArray === void 0 ? void 0 : refTextSpanArray.current;
    if (spanArray) {
      var maxHeight = 0;
      spanArray.forEach(function (v) {
        maxHeight = Math.max(v === null || v === void 0 ? void 0 : v.clientHeight, maxHeight);
      });
      setLabelHeight(maxHeight);
    }
  }, []);
  useEffect(function () {
    value !== undefined && updateIndexState(scales.findIndex(function (scale) {
      return scale[valueKey] === value;
    }));
  }, [value]);
  var onDataChange = function onDataChange(index, e) {
    var value = scales[index];
    updateIndexState(index);
    onChange === null || onChange === void 0 || onChange(value, e);
  };
  var renderDots = function renderDots() {
    return /*#__PURE__*/React.createElement("div", {
      id: "scaleSelect-".concat(id),
      className: "".concat(ROOT_PREFIX, "-dots"),
      role: "radiogroup",
      "aria-activedescendant": hasFocus ? "scaleSelect-".concat(id, "-option-").concat(selectedIndex) : undefined,
      "aria-labelledby": "scaleSelect-".concat(id, "-label"),
      tabIndex: disabled ? -1 : 0,
      onKeyUp: function onKeyUp(e) {
        return onKeyPress(e, ['Tab'], function () {
          if (selectedIndex === -1) updateIndexState(0);
          setHasFocus(true);
        });
      },
      onKeyDown: function onKeyDown(e) {
        return handleKeyDown(e);
      },
      onBlur: function onBlur() {
        return setHasFocus(false);
      }
    }, scales.map(function (v, i) {
      var selected = i === selectedIndex;
      return /*#__PURE__*/React.createElement("label", {
        id: "scaleSelect-".concat(id, "-selectableArea-").concat(i),
        key: v[valueKey],
        className: classnames("".concat(ROOT_PREFIX, "-focus-area"), hasFocus && selected && "".concat(ROOT_PREFIX, "-focus-visible")),
        onClick: !selected ? function (e) {
          return onDataChange(i, e);
        } : void 0
      }, /*#__PURE__*/React.createElement(IconButton, {
        id: "scaleSelect-".concat(id, "-option-").concat(i),
        role: "radio",
        "aria-checked": selected,
        "aria-label": v[ariaKey] || v[labelKey],
        className: classnames("".concat(ROOT_PREFIX, "-dot-area"), selected && "".concat(ROOT_PREFIX, "-dot-area-selected")),
        size: "large",
        disabled: disabled,
        tabIndex: -1
      }, /*#__PURE__*/React.createElement("span", {
        className: "".concat(ROOT_PREFIX, "-dot")
      })), /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true,
        className: classnames("".concat(ROOT_PREFIX, "-label"), selected && "".concat(ROOT_PREFIX, "-label-selected")),
        ref: getRef,
        onClick: !disabled && !selected ? function (e) {
          return onDataChange(i, e);
        } : void 0
      }, v[labelKey]));
    }));
  };
  return /*#__PURE__*/React.createElement(StyledScaleContainer, {
    className: classnames("".concat(ROOT_PREFIX, "-root"), {
      'Embers-disabled': disabled
    })
  }, /*#__PURE__*/React.createElement("p", {
    id: "scaleSelect-".concat(id, "-label"),
    "aria-hidden": true,
    hidden: labelHidden,
    className: "".concat(ROOT_PREFIX, "-title")
  }, label), /*#__PURE__*/React.createElement(StyledScaleBox, {
    className: "".concat(ROOT_PREFIX, "-scale-root"),
    style: _objectSpread({
      minWidth: 48 * scales.length,
      minHeight: 24 + labelHeight
    }, style)
  }, renderDots(), /*#__PURE__*/React.createElement("div", {
    className: "".concat(ROOT_PREFIX, "-scale-line")
  })));
}